import {useState} from 'react';

import {Field, FieldArray, Form, Formik} from 'formik';
import {useTranslation} from 'react-i18next';

import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UndoIcon from '@mui/icons-material/Undo';
import {Accordion, AccordionDetails, AccordionSummary, Box, Grid, IconButton, Typography} from '@mui/material';

import {CompanyProfileDialogButtons} from '../CompanyProfileDialogButtons';

import {DachCountrySelect} from '../../../../../components/DachCountrySelect/DachCountrySelect';
import {EmpionButton} from '../../../../../components/EmpionButton/EmpionButton';
import {EmpionTextField} from '../../../../../components/EmpionTextField/EmpionTextField';
import {empionOrange} from '../../../../../layouts/theme/EmpionColors';
import {CompanyLocation} from '../../../../../types/shared-types';
import {CompanyProfile} from '../../types';

type CompanyProfileLocationsFormPropTypes = {
  locations: CompanyLocation[];
  onCancel(): void;
  onSubmit(upserted: CompanyProfile['locations'], deleted: Set<string>): void;
};

export const CompanyProfileLocationsForm = (props: CompanyProfileLocationsFormPropTypes) => {
  const {t} = useTranslation();
  const [deletedIds, setDeletedIds] = useState<Set<string>>(new Set());

  const handleCancel = () => {
    props.onCancel();
  };

  const onFormikSubmit = async (values: any) => {
    props.onSubmit(values.locations as CompanyProfile['locations'], deletedIds);
  };

  const createEmptyLocation = () => ({
    street: '',
    houseNumber: '',
    zipCode: '',
    city: '',
    additionalInfo: '',
    country: '',
  });

  const isXsScreen = window.innerWidth < 600;

  // Here we make sure that we do not add the same to be deleted resourceId multiple times
  const toggleDeletion = (locationId: string) => {
    setDeletedIds((list) => {
      if (list.has(locationId)) {
        list.delete(locationId);
        return new Set(list);
      }

      return new Set(list.add(locationId));
    });
  };

  const isMarkedForDeletion = (locationId: string): boolean => deletedIds.has(locationId);

  return (
    <Formik initialValues={{locations: props.locations}} onSubmit={onFormikSubmit}>
      {(formik) => (
        <Form>
          <FieldArray name='locations'>
            {(listHelpers) => (
              <>
                {formik.values?.locations?.map((location, index) => (
                  <Accordion
                    key={location.resourceId}
                    disableGutters
                    elevation={0}
                    sx={{
                      border: `1px solid ${empionOrange[3]}`,
                      borderRadius: '12px',
                      boxShadow: 'none',
                      mb: 1.5,
                      overflow: 'hidden',
                      '&:before': {display: 'none'},
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          width: '45%',
                          flexShrink: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontWeight: 'bold',
                          marginRight: '16px',
                        }}
                      >
                        {isMarkedForDeletion(location.resourceId) && (
                          <ClearIcon sx={{verticalAlign: 'middle', color: 'red'}} />
                        )}
                        {location.city || t('companyProfile.locations.accordion.city')}
                      </Typography>
                      <Typography
                        sx={{
                          width: '33%',
                          color: 'text.secondary',
                          flexShrink: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {isXsScreen
                          ? location.street.slice(0, 10) || t('companyProfile.locations.accordion.street')
                          : location.street || t('companyProfile.locations.accordion.street')}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          {location.resourceId && (
                            <Field type='hidden' name={`locations.${index}.resourceId`} value={location.resourceId} />
                          )}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Field
                            type='text'
                            name={`locations.${index}.street`}
                            label={t('companyProfile.locations.accordion.street')}
                            component={EmpionTextField}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Field
                            type='text'
                            name={`locations.${index}.houseNumber`}
                            label={t('companyProfile.locations.accordion.houseNumber')}
                            component={EmpionTextField}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Field
                            type='text'
                            name={`locations.${index}.additionalInfo`}
                            label={t('companyProfile.locations.accordion.additionalInfo')}
                            component={EmpionTextField}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Field
                            type='text'
                            name={`locations.${index}.zipCode`}
                            label={t('companyProfile.locations.accordion.zipCode')}
                            component={EmpionTextField}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Field
                            type='text'
                            name={`locations.${index}.city`}
                            label={t('companyProfile.locations.accordion.city')}
                            component={EmpionTextField}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <DachCountrySelect
                            key={`locations.${index}.country`}
                            name={`locations.${index}.country`}
                            label={t('companyProfile.locations.accordion.country')}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Box display='flex' justifyContent='flex-end'>
                            <EmpionButton
                              variant='secondary'
                              size='small'
                              startIcon={isMarkedForDeletion(location.resourceId) ? <UndoIcon /> : <DeleteIcon />}
                              onClick={() =>
                                location.resourceId ? toggleDeletion(location.resourceId) : listHelpers.remove(index)
                              }
                            >
                              {isMarkedForDeletion(location.resourceId)
                                ? t('companyProfile.locations.accordion.buttonUndo')
                                : t('companyProfile.locations.accordion.buttonDelete')}
                            </EmpionButton>
                          </Box>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                ))}

                <Grid item>
                  <IconButton onClick={() => listHelpers.push(createEmptyLocation())}>
                    <AddIcon />
                  </IconButton>
                </Grid>

                {/* Buttons save & cancel */}
                <Grid item container justifyContent={'flex-end'}>
                  <CompanyProfileDialogButtons onCancel={handleCancel} />
                </Grid>
              </>
            )}
          </FieldArray>
        </Form>
      )}
    </Formik>
  );
};
